import React from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Sparkles, ArrowRight, Shield, Mail, Server } from 'lucide-react';

const Pillar = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#1F3D2B]/5 flex items-center justify-center">
      <Icon className="w-4 h-4 text-[#1F3D2B]" />
    </div>
    <div>
      <p className="font-medium text-[#1F3D2B] mb-1">{title}</p>
      <p className="text-sm text-stone-600 leading-relaxed">{children}</p>
    </div>
  </div>
);

const FeesPage = () => {
  return (
    <div className="min-h-[80vh] bg-stone-50 py-16 px-4" data-testid="fees-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1 bg-[#1F3D2B]/5 text-[#1F3D2B] text-xs tracking-[0.2em] uppercase rounded-full mb-3">
            VinePass Transparency
          </div>
          <h1
            className="font-serif text-4xl sm:text-5xl text-[#1F3D2B] mb-4"
            data-testid="fees-heading"
          >
            Pricing &amp; Fees
          </h1>
          <p
            className="text-[#1F3D2B] font-medium max-w-xl mx-auto leading-relaxed mb-3"
            data-testid="fees-promise"
          >
            No hidden fees. The price you see at checkout is the final price.
          </p>
          <p className="text-stone-600 max-w-xl mx-auto leading-relaxed">
            A simple, transparent fee model — designed to keep great experiences accessible
            and our platform reliable.
          </p>
        </div>

        {/* Headline range — gives buyers a clear expectation without baking
            specific tier amounts into the page (those still live in code). */}
        <div
          className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 sm:px-12 py-6 mb-6 text-center"
          data-testid="fees-range-banner"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] mb-2">Service Fee</p>
          <p className="text-2xl sm:text-3xl font-serif text-[#1F3D2B]">
            R10 – R20 <span className="text-base text-stone-500 font-sans">per ticket</span>
          </p>
          <p className="text-xs text-stone-500 mt-2">
            The exact fee depends on the ticket price and is always shown clearly before you pay.
          </p>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-stone-700 max-w-md mx-auto text-left">
            <li className="flex justify-between gap-4"><span>Under R150</span><span className="font-medium text-[#1F3D2B]">R10</span></li>
            <li className="flex justify-between gap-4"><span>R150 – R299</span><span className="font-medium text-[#1F3D2B]">R12</span></li>
            <li className="flex justify-between gap-4"><span>R300 – R599</span><span className="font-medium text-[#1F3D2B]">R15</span></li>
            <li className="flex justify-between gap-4"><span>R600 and above</span><span className="font-medium text-[#1F3D2B]">R20</span></li>
          </ul>
        </div>

        <div className="space-y-6">
          {/* For Guests */}
          <section
            className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 sm:px-12 py-10"
            data-testid="fees-guests-section"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#1F3D2B] text-white flex items-center justify-center">
                <Ticket className="w-5 h-5" />
              </div>
              <h2 className="font-serif text-2xl text-[#1F3D2B]">For Guests &amp; Ticket Buyers</h2>
            </div>
            <p className="text-stone-700 leading-relaxed mb-3">
              A small service fee may be applied per ticket at checkout — depending on how
              the event organiser chooses to structure their pricing.
            </p>
            <p className="text-stone-700 leading-relaxed mb-3">This means:</p>
            <ul className="text-stone-700 leading-relaxed mb-6 space-y-1.5 pl-5 list-disc marker:text-[#C9A27A]">
              <li>The fee can be paid by the guest</li>
              <li>Shared between the guest and organiser</li>
              <li>Or fully absorbed by the organiser</li>
            </ul>
            <p className="text-stone-700 leading-relaxed mb-6">
              You&apos;ll always see the exact final price before you pay — no surprises.
              The fee covers:
            </p>
            <div className="space-y-4">
              <Pillar icon={Shield} title="Secure payments">
                Your card and EFT details are protected by PayFast&apos;s PCI-DSS infrastructure
                — VinePass never sees your card number.
              </Pillar>
              <Pillar icon={Mail} title="Ticket delivery">
                Instant email confirmation with a unique QR code per seat, plus a downloadable
                PDF ticket.
              </Pillar>
              <Pillar icon={Server} title="Platform infrastructure">
                Hosting, customer support, fraud prevention, and the ongoing work of keeping
                VinePass running.
              </Pillar>
            </div>
            <p className="text-xs text-stone-500 mt-6 pt-5 border-t border-stone-100">
              You&apos;ll see the exact fee on the checkout summary before you confirm.
              Free tickets never incur a service fee.
            </p>
          </section>

          {/* For Organisers */}
          <section
            className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 sm:px-12 py-10"
            data-testid="fees-organisers-section"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#C9A27A] text-white flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <h2 className="font-serif text-2xl text-[#1F3D2B]">For Event Organisers</h2>
            </div>
            <p className="text-stone-700 leading-relaxed mb-6">
              Listing an event on VinePass is built around a single, transparent per-ticket
              booking fee. That&apos;s it.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'No upfront costs',
                'No monthly fees',
                'No hidden charges',
                'No long-term contracts',
                'Flexible per-ticket booking fee options',
                'Choose how fees are applied (guest pays, split, or absorb)',
                'Free tickets cost you nothing',
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-2 text-sm text-stone-700"
                >
                  <span className="text-[#1F3D2B] mt-0.5">✓</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-stone-500 mt-6 pt-5 border-t border-stone-100">
              The current per-ticket fee structure is shared with you during onboarding and
              shown alongside every payout statement. Talk to us if you&apos;re running a
              high-volume series — we&apos;re happy to discuss.
            </p>
          </section>

          {/* Refunds */}
          <section
            className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 sm:px-12 py-10"
            data-testid="fees-refunds-section"
          >
            <h2 className="font-serif text-2xl text-[#1F3D2B] mb-3">Refunds</h2>
            <p className="text-stone-700 leading-relaxed">
              Event organisers set their own refund policies. Where a refund is granted on
              the ticket price, the booking fee remains non-refundable as it covers payment
              processing and ticket delivery costs already incurred.
            </p>
            <Link
              to="/refund-policy"
              className="inline-flex items-center mt-5 text-sm text-[#1F3D2B] hover:text-[#172E20] group"
              data-testid="fees-refund-policy-link"
            >
              Read the full Refund Policy
              <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </section>

          {/* Contact CTA */}
          <div className="text-center pt-6">
            <p className="text-sm text-stone-500 mb-3">Questions about pricing?</p>
            <a
              href="mailto:info@vinepass.co.za"
              className="inline-block bg-[#1F3D2B] text-white text-sm rounded-full px-6 py-2.5 hover:bg-[#172E20] transition-colors"
              data-testid="fees-contact-button"
            >
              Get in touch
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeesPage;
